import { useEffect, useState, useCallback } from 'react';
import { Grid, Box, Button, TextField, Typography, Checkbox, FormControlLabel, Divider, Link } from '@mui/material';
import { useLocation } from 'react-router';
import { ErrorMessageBox, LinkStyled } from './loginStyles';
import { ResetPassword } from '../resetPassword/resetPassword';
import { getStatusError } from '../../../helpers/errorMessage';
import { useTranslation } from 'react-i18next';
import { FacebookSignIn, GoogleSignIn } from '../../../controls';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { GoogleLoginError, LoginFieldValues, RegistrationProps } from '../../../interfaces/interfaces';
import { ButtonLoadingIndicator } from '../../../controls';
import {
  authenticate,
  authenticateFromApple,
  authenticateFromFacebook,
  authenticateFromGoogle,
  removeLoginError,
  setRememberMe,
} from '../../../redux/reducers/authReducer';
import { RootState } from '../../../redux/store';
import * as externalLogin from '../../../helpers/externalLogin';
import { AppleSignIn } from '../externalLogin/appleSignIn';
import { ExternalAuthenticationRequest } from 'orderme-api-integration-client';
import { useRouteNavigator } from '../../../routes/useRouteNavigator';
import LoginGridItem from './loginGridItem';
import { removeRedirectAfterLogin } from '../../../helpers/localStorageService';

const inputValues = {
  loginName: '',
  password: '',
};

interface LoginProps {
  hideTitle?: boolean;
}

export function Login({ hideTitle }: LoginProps) {
  const dispatch = useAppDispatch();
  const routeNavigator = useRouteNavigator();
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);
  const isAuthenticating = useAppSelector((state: RootState) => state.authState.loading);
  const authErrorMessage = useAppSelector((state: RootState) => state.authState.error);
  const rememberMe = useAppSelector((state: RootState) => state.authState.rememberMe);
  const [textFieldValues, setTextFieldValues] = useState<LoginFieldValues>(inputValues);
  const [showPswReset, setShowPswReset] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>(null);
  const { search } = useLocation();
  const query = new URLSearchParams(search);
  const [googleUserGrantRequired, setGoogleUserGrantRequired] = useState(false);
  const [googleLoginHint, setGoogleLoginHint] = useState<string>(null);

  const { t } = useTranslation();

  const handleFieldChange = (e: any) => {
    const { name, value } = e.target;
    const fieldValue = { [name]: value } as Pick<LoginFieldValues, keyof LoginFieldValues>;

    setTextFieldValues({
      ...textFieldValues,
      ...fieldValue,
    });
  };

  const login = () => {
    if (isButtonDisabled || isAuthenticating) {
      return;
    }
    dispatch(authenticate({ input: textFieldValues.loginName, password: textFieldValues.password }));
  };

  const validate = useCallback(() => {
    if (Object.values(textFieldValues).every((value) => value !== '')) {
      setIsButtonDisabled(false);
    } else {
      setIsButtonDisabled(true);
    }
  }, [textFieldValues]);

  useEffect(() => {
    validate();
  }, [validate]);

  useEffect(() => {
    dispatch(removeLoginError());
    const client = query.get('client');
    if (client === 'facebook') {
      handleFacebookSignIn(query.get('access_token'), query.get('state'));
    } else if (client === 'google') {
      handleGoogleSignIn(query.get('access_token'), query.get('state'), query.get('code'));
    } else if (client === 'apple') {
      handleAppleSignIn(
        query.get('access_token'),
        query.get('state'),
        query.get('code'),
        query.get('firstName'),
        query.get('lastName')
      );
    }

    externalLogin.removeAllStates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setErrorMessage(authErrorMessage);
  }, [authErrorMessage]);

  const onLoginFailure = (err: any) => {
    var msg = getStatusError(err.status);
    setErrorMessage(msg);
  };

  const openResetPassword = () => {
    setShowPswReset(true);
  };

  const closeResetPassword = () => {
    setShowPswReset(false);
  };

  const handleFacebookSignIn = async (accessToken?: string, state?: string) => {
    const signInState = externalLogin.getFacebookState();

    if (!accessToken || state !== signInState) {
      navigateToLogin();
      return;
    }

    try {
      await dispatch(
        authenticateFromFacebook({ accessToken: accessToken, state: state } as ExternalAuthenticationRequest)
      ).unwrap();
    } catch (err) {
      console.log(err);
      const registrationProps = await externalLogin.getFacebookRegistrationProps(accessToken, state);
      navigateToRegistration(registrationProps);
    }
  };

  const handleGoogleSignIn = async (accessToken: string, state: string, code: string) => {
    const googleState = externalLogin.getGoogleState();
    externalLogin.removeGoogleState();
    var googleToken = externalLogin.getGoogleTokenProps(accessToken);
    if (!accessToken || !code || state !== googleState.state || googleToken?.nonce !== googleState.nonce) {
      navigateToLogin();
      return;
    }

    try {
      const authRequest = { accessToken: accessToken, state: state, code: code } as ExternalAuthenticationRequest;
      await dispatch(authenticateFromGoogle(authRequest)).unwrap();
    } catch (err) {
      console.log(err);
      const loginError = err as GoogleLoginError;
      if (loginError?.userGrantRequired) {
        setGoogleLoginHint(googleToken.email);
        setGoogleUserGrantRequired(true);
        return;
      }
      const registrationProps = externalLogin.getGoogleRegistrationProps(accessToken, state, code);
      navigateToRegistration(registrationProps);
    }
  };

  const handleAppleSignIn = async (
    accessToken?: string,
    state?: string,
    code?: string,
    firstName?: string,
    lastName?: string
  ) => {
    const appleState = externalLogin.getAppleState();
    externalLogin.removeAppleState();

    var appleToken = externalLogin.getAppleTokenProps(accessToken);

    if (!accessToken || !code || state !== appleState?.state || appleToken?.nonce !== appleState.nonce) {
      navigateToLogin();
      return;
    }

    try {
      const authRequest = { accessToken: accessToken, state: state, code: code } as ExternalAuthenticationRequest;
      await dispatch(authenticateFromApple(authRequest)).unwrap();
    } catch (err) {
      console.log(err);
      const registrationProps = externalLogin.getAppleRegistrationProps(accessToken, state, code, firstName, lastName);
      navigateToRegistration(registrationProps);
    }
  };

  const navigateToRegistration = (registrationProps: RegistrationProps) => {
    routeNavigator.goToRegistration({ state: registrationProps, replace: true });
  };

  const navigateToLogin = () => {
    removeRedirectAfterLogin();
    routeNavigator.goToLogin({ replace: true });
  };

  return (
    <Box sx={{ backgroundColor: 'white', color: 'black', borderRadius: '15px' }}>
      <Grid
        container
        direction="column"
        sx={{
          justifyContent: 'center',
          alignItems: 'center',
          p: '20px',
          maxWidth: '400px',
          mx: 'auto',
        }}
      >
        {!hideTitle && (
          <LoginGridItem disableTopSpacing>
            <Typography sx={{ textAlign: 'left', fontSize: '24px' }}>{t('account:login')}</Typography>
          </LoginGridItem>
        )}
        <LoginGridItem>
          <TextField
            fullWidth
            slotProps={{
              input: {
                sx: {
                  borderRadius: '15px',
                },
              },
            }}
            name="loginName"
            onChange={handleFieldChange}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !isButtonDisabled && !isAuthenticating) {
                e.preventDefault();
                login();
              }
            }}
            label={t('account:loginInput')}
            variant="outlined"
          />
        </LoginGridItem>
        <LoginGridItem>
          <TextField
            fullWidth
            slotProps={{
              input: {
                sx: {
                  borderRadius: '20px',
                },
              },
            }}
            name="password"
            onChange={handleFieldChange}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !isButtonDisabled && !isAuthenticating) {
                e.preventDefault();
                login();
              }
            }}
            label={t('account:password')}
            type="password"
            variant="outlined"
          />
        </LoginGridItem>
        <LoginGridItem disableBottomSpacing disableTopSpacing>
          {errorMessage && <ErrorMessageBox>{errorMessage}</ErrorMessageBox>}
        </LoginGridItem>
        <LoginGridItem disableBottomSpacing disableTopSpacing>
          <Grid container sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
            <FormControlLabel
              control={<Checkbox checked={rememberMe} onChange={() => dispatch(setRememberMe(!rememberMe))} />}
              label={t('account:rememberMe')}
            />
            <Link
              color="secondary"
              sx={{
                fontSize: '18px',
                '&:hover': {
                  cursor: 'pointer',
                },
              }}
              underline="hover"
              onClick={() => openResetPassword()}
            >
              {t('account:forgotPassword')}
            </Link>
          </Grid>
        </LoginGridItem>
        <LoginGridItem>
          <Button
            sx={{
              width: '100%',
              backgroundColor: 'button.secondary.background',
              color: 'button.primary.color',
              height: '50px',
            }}
            onClick={() => login()}
            disabled={isButtonDisabled || isAuthenticating}
            loading={isAuthenticating}
            loadingIndicator={<ButtonLoadingIndicator />}
            variant="contained"
          >
            <Box>{t('account:login')}</Box>
          </Button>
        </LoginGridItem>
        <LoginGridItem>
          <Divider sx={{ fontSize: '18px' }}>{t('account:or')}</Divider>
        </LoginGridItem>
        <LoginGridItem>
          <Grid container spacing={2} sx={{ justifyContent: 'center', alignItems: 'center' }}>
            <Grid>
              <FacebookSignIn isVisible={!isAuthenticating} onError={(err) => onLoginFailure(err)} />
            </Grid>
            <Grid>
              <GoogleSignIn
                isVisible={!isAuthenticating}
                onError={(err) => onLoginFailure(err)}
                userGrantRequired={googleUserGrantRequired}
                loginHint={googleLoginHint}
              />
            </Grid>
            <Grid>
              <AppleSignIn isVisible={!isAuthenticating} onError={(err) => onLoginFailure(err)} />
            </Grid>
          </Grid>
        </LoginGridItem>
        <LoginGridItem>
          <Grid container sx={{ justifyContent: 'center' }}>
            <Typography sx={{ fontSize: '18px' }}>{t('account:missingAccount')}</Typography>
            <LinkStyled underline="hover" onClick={() => routeNavigator.goToRegistration()}>
              {t('account:register')}
            </LinkStyled>
          </Grid>
        </LoginGridItem>
      </Grid>
      <ResetPassword open={showPswReset} onClose={closeResetPassword} />
    </Box>
  );
}
